import { ReactNode, Ref, useRef } from "react"

import { ReactComponent as IconExport } from "@common_assets/svg/export.svg"
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { useExportNodeAsPng } from "hooks/exportImage"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"

import { dashboardTitleSx } from "./AdminDashboard.styles"
import { formatRange } from "./CustomRangeDialog"

export interface IDashboardSectionExport {
  // File slug, e.g. "store-items"
  section: string
  // The period from the section's own response
  dateFrom?: string
  dateTo?: string
  detail?: string
  disabled: boolean
}

const DashboardSection = ({
  title,
  question,
  questionGap = 0,
  icon,
  exportImage,
  titleRef,
  children,
}: {
  title: string
  // Makes the title focusable from code, so focus can be moved to the section.
  titleRef?: Ref<HTMLHeadingElement>
  // Decorative, beside the title
  icon?: ReactNode
  question: string
  questionGap?: number
  exportImage?: IDashboardSectionExport
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const cardRef = useRef<HTMLElement>(null)
  const { exportPng, isExporting } = useExportNodeAsPng(
    cardRef,
    `admin-dashboard_${exportImage?.section}_${exportImage?.dateFrom}_${exportImage?.dateTo}`,
  )

  const handleExport = () => {
    if (isExporting) return
    exportPng().catch(() =>
      showSnackbar({
        variant: "error",
        title: t("adminDashboard.downloadAsImageError"),
      }),
    )
  }

  const period =
    exportImage && formatRange(exportImage.dateFrom, exportImage.dateTo)

  return (
    <Box
      ref={cardRef}
      component="section"
      aria-label={title}
      sx={{
        bgcolor: "mockup.neutral100",
        border: "1px solid",
        borderColor: "mockup.neutral90",
        borderRadius: "16px",
        p: { mobile: 2, tablet: 3 },
      }}
    >
      <Stack direction="row" alignItems="flex-start" columnGap={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" columnGap={1.5}>
            {icon}
            <Typography
              variant="body1"
              ref={titleRef}
              component="h3"
              tabIndex={titleRef ? -1 : undefined}
              color="mockup.neutral0"
              sx={{ ...dashboardTitleSx, "&:focus": { outline: "none" } }}
            >
              {title}
            </Typography>
          </Stack>
          {/* Only in the image, so it still makes sense out of context */}
          {isExporting && (
            <Typography variant="body2" color="mockup.neutral30" mt={0.5}>
              {exportImage?.detail
                ? `${period} · ${exportImage.detail}`
                : period}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="mockup.neutral30"
            sx={{
              mt: questionGap,
              // A "\n" in the copy starts a new line
              whiteSpace: "pre-line",
              fontWeight: 500,
              lineHeight: "128%",
              letterSpacing: "-0.07px",
            }}
          >
            {question}
          </Typography>
        </Box>
        {exportImage && (
          <Tooltip title={t("adminDashboard.downloadAsImage")} describeChild>
            {/* The span keeps the tooltip working while the button is disabled */}
            <Box component="span" data-export-ignore sx={{ flexShrink: 0 }}>
              <IconButton
                aria-label={t("adminDashboard.downloadSectionAsImage", {
                  section: title,
                })}
                disabled={exportImage.disabled}
                // Not `disabled` while exporting, so keyboard focus stays on it
                aria-disabled={isExporting}
                onClick={handleExport}
                sx={{ color: "mockup.neutral30", width: 40, height: 40 }}
              >
                {isExporting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Box
                    component={IconExport}
                    aria-hidden
                    sx={{ width: 24, height: 24 }}
                  />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
      <Box mt={2}>{children}</Box>
    </Box>
  )
}

export default DashboardSection
